import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import {applyPlugin } from 'jspdf-autotable';
import { useNotificationContext } from '../contexts/NotificationContext';


// Apply jsPDF AutoTable plugin
applyPlugin(jsPDF);

const PDFBilan = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPdf, setCurrentPdf] = useState(null);
  
  const [filters, setFilters] = useState({
    academicYear: '',
    semester: 1
  });

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get('/api/academic-years');
      setAcademicYears(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des années:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const generatePDF = async () => {
    if (!filters.academicYear) {
      showError('Veuillez sélectionner l\'année universitaire');
      return;
    }

    setLoading(true);
    try {
      const bilanData = await fetchBilanData();
      const pdf = createPDF(bilanData);
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setCurrentPdf(pdf);
      showSuccess('PDF généré avec succès');
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      showError('Erreur lors de la génération du PDF');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (currentPdf) {
      currentPdf.save(`bilan-enseignants-${Date.now()}.pdf`);
    }
  };

  const fetchBilanData = async () => {
    try {
      // Récupérer les sessions pour le semestre
      const sessionsRes = await axios.get(`/api/sessions?semester=${filters.semester}&academicYear=${filters.academicYear}&limit=10000`);
      const sessions = sessionsRes.data;

      // Récupérer tous les enseignants
      const teachersRes = await axios.get('/api/teachers');
      const teachers = teachersRes.data;

      // Calculer le bilan pour chaque enseignant avec la même logique que PDFEnseignants
      const bilanData = teachers.map(teacher => {
        const teacherSessions = sessions.filter(session => session.teacher?._id === teacher._id);
        
        // Calculer les heures par type selon la règle:
        // Séance cours : Cours 1h, TD 0.5h
        // Séance TP : 1.5h TP
        let totalHours = 0, coursHours = 0, tdHours = 0, tpHours = 0;
        
        teacherSessions.forEach(session => {
          if (session.type === 'LECTURE') {
            // Séance cours : 1h cours + 0.5h TD = 1.5h total
            totalHours += 1.5;
            coursHours += 1;
            tdHours += 0.5;
          } else if (session.type === 'PRACTICAL') {
            // Séance TP : 1.5h TP
            totalHours += 1.5;
            tpHours += 1.5;
          } else if (session.type === 'TUTORIAL') {
            // Séance TD : 1.5h TD
            totalHours += 1.5;
            tdHours += 1.5;
          }
        });

        return {
          teacher,
          totalHours: totalHours.toFixed(1),
          coursHours: coursHours.toFixed(1),
          tdHours: tdHours.toFixed(1),
          tpHours: tpHours.toFixed(1),
          totalSessions: teacherSessions.length
        };
      }).filter(data => data.totalSessions > 0); // Ne garder que ceux avec des séances

      return bilanData.sort((a, b) => b.totalHours - a.totalHours);
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      return [];
    }
  };

  const createPDF = (bilanData) => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text('Bilan Global des Enseignants', 20, 20);
    
    const selectedYear = academicYears.find(y => y._id === filters.academicYear);
    
    doc.setFontSize(12);
    doc.text(`Année: ${selectedYear?.name || ''} - Semestre ${filters.semester}`, 20, 35);

    // Préparer les données du tableau
    const tableData = bilanData.map((data, index) => [
      index + 1,
      `${data.teacher.firstName} ${data.teacher.lastName}`,
      data.teacher.grade?.name || '',
      data.totalHours,
      data.coursHours,
      data.tdHours,
      data.tpHours,
      data.totalSessions
    ]);

    // Calculer les totaux
    const totals = bilanData.reduce((acc, data) => ({
      totalHours: acc.totalHours + parseFloat(data.totalHours),
      coursHours: acc.coursHours + parseFloat(data.coursHours),
      tdHours: acc.tdHours + parseFloat(data.tdHours),
      tpHours: acc.tpHours + parseFloat(data.tpHours),
      totalSessions: acc.totalSessions + data.totalSessions
    }), { totalHours: 0, coursHours: 0, tdHours: 0, tpHours: 0, totalSessions: 0 });

    // Ajouter la ligne de total
    tableData.push([
      '',
      'TOTAL',
      '',
      totals.totalHours.toFixed(1),
      totals.coursHours.toFixed(1),
      totals.tdHours.toFixed(1),
      totals.tpHours.toFixed(1),
      totals.totalSessions
    ]);

    doc.autoTable({
      head: [['N°', 'Enseignant', 'Grade', 'Total H', 'H Cours', 'H TD', 'H TP', 'Séances']],
      body: tableData,
      startY: 45,
      tableWidth: 'wrap',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [102, 126, 234] },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 50 },
        2: { cellWidth: 35 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 25 }
      },
      didParseCell: function (data) {
        // Mettre en gras la ligne de total
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240];
        }
      }
    });

    // Ajouter les informations sur la formule
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(10);
    doc.text('Note: Une séance de cours de 1,5h = 1h cours + 0,5h TD', 20, finalY);
    doc.text(`Total enseignants: ${bilanData.length - 1}`, 20, finalY + 10);

    return doc;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Génération PDF - Bilan Global</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Année universitaire</label>
              <select 
                name="academicYear" 
                value={filters.academicYear} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Semestre</label>
              <select 
                name="semester" 
                value={filters.semester} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={1}>Semestre 1</option>
                <option value={2}>Semestre 2</option>
              </select>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-900 mb-2">📊 Formule de calcul</h3>
            <p className="text-sm text-blue-800">Une séance de cours de 1,5h correspond à 1h de cours + 0,5h de TD</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={generatePDF} 
              disabled={loading || !filters.academicYear}
              className={`px-6 py-3 text-sm font-medium text-white rounded-lg focus:ring-2 focus:ring-primary-500 ${
                loading || !filters.academicYear
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Génération...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  📄 Générer PDF
                </span>
              )}
            </button>
            
            {pdfUrl && (
              <button 
                onClick={downloadPDF}
                className="px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500"
              >
                <span className="flex items-center justify-center gap-2">
                  💾 Télécharger PDF
                </span>
              </button>
            )}
          </div>
        </div>

        {pdfUrl && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">👁️ Aperçu du document</h3>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <iframe 
                src={pdfUrl}
                className="w-full h-96"
                title="Aperçu PDF"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFBilan;